import React from 'react';
import {
  Box,
  Grid,
  Card,
  Stack,
  Divider,
  Typography,
  FormControl,
  Select,
  MenuItem,
  Chip,
  LinearProgress,
} from '@mui/material';

import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { FaCheckCircle, FaCircle } from 'react-icons/fa';
import { LuUsersRound, LuTicket } from 'react-icons/lu';
import { IoShieldCheckmark } from 'react-icons/io5';
import { LiaCoinsSolid } from "react-icons/lia";
import { GrPieChart } from "react-icons/gr";
import { IoMdInformationCircleOutline } from 'react-icons/io';

const SectionNumber = ({ n }) => (
  <Box
    sx={{
      width: 22,
      height: 22,
      borderRadius: '50%',
      border: `1.5px solid #3FBEAC`,
      color: '#3FBEAC',
      bgcolor: '#ECF9F7',
      fontSize: 12,
      fontWeight: 700,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    }}
  >
    {n}
  </Box>
);

const MiniStat = ({ icon, iconColor, iconBg, label, value }) => (
  <Box display={"flex"}  alignItems="center" gap={1} sx={{ border: '1px solid #E8E8E8', borderRadius: 2, p: 1.5 }} height={"100%"}>
    <Box
      sx={{
        width: 34,
        height: 34,
        borderRadius: '50%',
        bgcolor: iconBg,
        color: iconColor,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      {icon}
    </Box>
    <Box>
      <Typography variant="caption" color="#535353" sx={{ display: 'block', fontSize:12 }}>
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={700}>
        {value}
      </Typography>
    </Box>
  </Box>
);

const BudgetUsageTab = ({ data }) => {
  const {
    budgetReview,
    budgetConsumption,
    fundingAllocation,
    budgetRedemptionSummary,
    budgetHistory,
    budgetHealth,
    fundingSplit,
    budgetRules,
    budgetAlerts,
  } = data;

  const currencyEGP = (n) => `${Number(n).toLocaleString()} EGP`;
  const consumptionPercent = Math.round((budgetConsumption.used / budgetConsumption.total) * 100);

  const HistoryChart = ({ chartData }) => (
    <ResponsiveContainer width="100%" height={235}>
      <AreaChart data={chartData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
        <defs>
          <linearGradient id="budgetHistoryFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#3FBEAC" stopOpacity={0.25} />
            <stop offset="95%" stopColor="#3FBEAC" stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="#E8E8E8" />
        <XAxis dataKey="date" tickLine={false} axisLine={false} tick={{ fontSize: 12, fill: '#8A94A6' }} />
        <YAxis
          tickLine={false}
          axisLine={false}
          tick={{ fontSize: 12, fill: '#8A94A6' }}
          tickFormatter={(v) => `${v / 1000}K`}
        />
        <Tooltip
          formatter={(value) => [currencyEGP(value), 'Consumed']}
          contentStyle={{ borderRadius: 8, border: '1px solid #E8E8E8', fontSize: 12 }}
        />
        <Area
          type="monotone"
          dataKey="amount"
          stroke="#1A1F2B"
          strokeWidth={2}
          fill="url(#budgetHistoryFill)"
          dot={{ r: 4, stroke: '#1A1F2B', strokeWidth: 2, fill: '#fff' }}
          activeDot={{ r: 5 }}
        />
      </AreaChart>
    </ResponsiveContainer>
  );

  return (
    <Grid container spacing={2.5} alignItems="stretch">
      {/* Left column */}
      <Grid item xs={12} md={8} sx={{ display: 'flex' }}>
        <Card variant="outlined" sx={{ borderRadius: 2, borderColor: '#E8E8E8', p: 1.5, width: '100%', height: '100%' }}>
          {/* Budget Review */}
          <Box my={1} borderBottom="1px solid #E8E8E8" py={2}>
            <Box display="flex" gap={1.5} alignItems="center">
              <SectionNumber n={1} />
              <Typography fontWeight={600}>Budget Review</Typography>
            </Box>
            <Typography variant="body2" color="#535353" sx={{ pl: 4.5, mt: 0.5 }}>
              Real-time overview of this promo code's budget and spending.
            </Typography>

            <Box sx={{ p: { xs: 1, sm: 2 } }}>
              <Grid container spacing={3}>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="#535353" sx={{ display: 'block', mb: 0.5 }}>
                    Total Budget
                  </Typography>
                  <Typography variant="body2" fontWeight={700}>
                    {currencyEGP(budgetReview.totalBudget)}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="#535353" sx={{ display: 'block', mb: 0.5 }}>
                    Consumed Budget
                  </Typography>
                  <Typography variant="body2" fontWeight={700}>
                    {currencyEGP(budgetReview.consumedBudget)}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="#535353" sx={{ display: 'block', mb: 0.5 }}>
                    Remaining Budget
                  </Typography>
                  <Typography variant="body2" fontWeight={700}>
                    {currencyEGP(budgetReview.remainingBudget)}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="#535353" sx={{ display: 'block', mb: 0.5 }}>
                    Utilization
                  </Typography>
                  <Typography variant="body2" fontWeight={700}>
                    {budgetReview.utilization}%
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          </Box>

          {/* Budget Consumption Progress */}
          <Box my={1} borderBottom="1px solid #E8E8E8" py={2}>
            <Box display="flex" gap={1.5} alignItems="center">
              <SectionNumber n={2} />
              <Typography fontWeight={600}>Budget Consumption Progress</Typography>
            </Box>
            <Typography variant="body2" color="#535353" sx={{ pl: 4.5, mt: 0.5 }}>
              Track how much of the budget has been used.
            </Typography>

            <Box sx={{ px: 0.5, pt: 3, pb: 1 }}>
              <LinearProgress
                variant="determinate"
                value={consumptionPercent}
                sx={{
                  height: 10,
                  borderRadius: 5,
                  bgcolor: '#CDCDCD',
                  '& .MuiLinearProgress-bar': { bgcolor: '#3FBEAC', borderRadius: 5 },
                }}
              />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={0.5} justifyContent="space-between" sx={{ mt: 1.5 }}>
                <Typography variant="body2" fontWeight={600}>
                  {currencyEGP(budgetConsumption.used)} used
                </Typography>
                <Typography variant="body2" color="#535353">
                  {currencyEGP(budgetConsumption.total)} total
                </Typography>
              </Stack>
            </Box>
          </Box>

          {/* Funding Allocation + Redemption Summary side by side */}
          <Grid container my={1} borderBottom="1px solid #E8E8E8" py={2}>
            <Grid item xs={12} sm={6} sx={{ pr: { sm: 2 } }}>
              <Box display="flex" gap={1.5} alignItems="center">
                <SectionNumber n={3} />
                <Typography fontWeight={600}>Funding Allocation</Typography>
              </Box>
              <Typography variant="body2" color="#535353" sx={{ pl: 4.5, mt: 0.5, mb: 2 }}>
                How the budget is funded between platform and provider.
              </Typography>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} p={1} alignItems="center" justifyContent={"center"} border={"1px solid #E8E8E8"} borderRadius={3}>
                <Box sx={{ position: 'relative', width: 120, height: 120, flexShrink: 0 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={[
                          { name: 'Platform', value: fundingAllocation.platform, color: '#3FBEAC' },
                          { name: 'Provider', value: fundingAllocation.provider, color: '#696969' },
                        ]}
                        dataKey="value"
                        nameKey="name"
                        innerRadius={40}
                        outerRadius={58}
                        paddingAngle={2}
                        startAngle={90}
                        endAngle={-270}
                      >
                        <Cell fill="#3FBEAC" stroke="none" />
                        <Cell fill="#696969" stroke="none" />
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                  <Box
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      textAlign: 'center',
                    }}
                  >
                    <Typography variant="subtitle1" fontWeight={700} lineHeight={1.1}>
                      {fundingAllocation.total.toLocaleString()}
                    </Typography>
                    <Typography variant="caption" color="#535353">
                      EGP
                    </Typography>
                  </Box>
                </Box>

                <Stack spacing={1}>
                  <Stack direction={"row"} spacing={1} alignItems="center">
                    <FaCircle size={8} color="#3FBEAC" />
                    <Box display={"flex"}  alignItems={"center"} gap={1} flexWrap="wrap">
                      <Typography variant="body2">Platform (ESTBN)</Typography>
                      <Typography variant="body2" fontWeight={700}>
                        {currencyEGP(fundingAllocation.platform)}
                      </Typography>
                    </Box>
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <FaCircle size={8} color="#696969" />
                    <Box display={"flex"}  alignItems={"center"} gap={1} flexWrap="wrap">
                      <Typography variant="body2">Provider</Typography>
                      <Typography variant="body2" fontWeight={700}>
                        {currencyEGP(fundingAllocation.provider)}
                      </Typography>
                    </Box>
                  </Stack>
                </Stack>
              </Stack>
            </Grid>

            <Grid item xs={12} sm={6} sx={{ pl: { sm: 2 }, borderLeft: { sm: '1px solid #E8E8E8' }, mt: { xs: 3, sm: 0 } }}>
              <Box display="flex" gap={1.5} alignItems="center">
                <SectionNumber n={4} />
                <Typography fontWeight={600}>Redemption Summary</Typography>
              </Box>
              <Typography variant="body2" color="#535353" sx={{ pl: 4.5, mt: 0.5, mb: 2 }}>
                Key redemption and discount insights.
              </Typography>

              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={6}>
                  <MiniStat
                    icon={<LuUsersRound size={18} />}
                    iconColor="#696969"
                    iconBg="#F8F8F8"
                    label="Total Usage Limit"
                    value={`${budgetRedemptionSummary.totalUsageLimit.toLocaleString()} Uses`}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <MiniStat
                    icon={<LuTicket size={18} />}
                    iconColor="#04B22E"
                    iconBg="#04B22E1A"
                    label="Remaining Uses"
                    value={budgetRedemptionSummary.remainingUses.toLocaleString()}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <MiniStat
                    icon={<LiaCoinsSolid size={18} />}
                    iconColor="#3E9FF6"
                    iconBg="#F0F8FF"
                    label="Avg. Discount Per User"
                    value={currencyEGP(budgetRedemptionSummary.avgDiscountPerUser)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <MiniStat
                    icon={<GrPieChart size={18} />}
                    iconColor="#FFCC00"
                    iconBg="#FFFAEA"
                    label="Usage Rate"
                    value={`${budgetRedemptionSummary.usageRate}%`}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          {/* Budget History */}
          <Box my={1} py={2}>
            <Box display="flex" alignItems="center" justifyContent="space-between" my={2} sx={{ flexWrap: 'wrap', gap: 2 }}>
              <Box>
                <Box display="flex" gap={1.5} alignItems="center">
                  <SectionNumber n={5} />
                  <Typography fontWeight={600}>Budget History</Typography>
                </Box>
                <Typography variant="body2" color="#535353" sx={{ pl: 4.5, mt: 0.5 }}>
                  Daily budget consumption over time.
                </Typography>
              </Box>
              <FormControl size="small" sx={{ '& .MuiOutlinedInput-root': { borderRadius: '6px', fontSize: 13 } }}>
                <Select defaultValue="30" IconComponent={KeyboardArrowDownIcon}>
                  <MenuItem value="30">Last 30 Days</MenuItem>
                  <MenuItem value="20">Last 20 Days</MenuItem>
                  <MenuItem value="10">Last 10 Days</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <HistoryChart chartData={budgetHistory} />
          </Box>
        </Card>
      </Grid>

      {/* Right column */}
      <Grid item xs={12} md={4} sx={{ display: 'flex' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', height: '100%' }}>
        {/* Budget Health */}
        <Card variant="outlined" sx={{ borderRadius: 4, borderColor: '#E8E8E8', p: 1.5 }}>
          <Box sx={{ p: 1, my: 0.5 }}>
            <Typography fontWeight={600}>Budget Health</Typography>
          </Box>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ p: 1, py: 2 }}>
              <IoShieldCheckmark color={"#3FBEAC"} size={25} />
            <Typography variant="body1" fontWeight={700} color="#159D89">
              {budgetHealth.status}
            </Typography>
          </Stack>
          <Typography variant="body2" color="#535353" sx={{ px: 1, pb: 1 }}>
            {budgetHealth.message}
          </Typography>
        </Card>

        {/* Funding Split */}
        <Card variant="outlined" sx={{ borderRadius: 2, borderColor: '#E8E8E8', p: 1.5, my: 2 }}>
          <Box sx={{ p: 1, my: 0.5, borderBottom: '1px solid #E8E8E8' }}>
            <Typography fontWeight={600}>Funding Spilt</Typography>
          </Box>
          <Stack sx={{ px: 1 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ py: 1.25 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <FaCircle size={8} color="#3FBEAC" />
                <Typography variant="body2">Platform (ESTBN)</Typography>
              </Stack>
              <Typography variant="body2" fontWeight={600}>
                {currencyEGP(fundingSplit.platform)}{' '}
                <Typography component="span" variant="body2" color="#535353">
                  ({fundingSplit.platformPercent}%)
                </Typography>
              </Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ py: 1.25 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <FaCircle size={8} color="#696969" />
                <Typography variant="body2">Provider</Typography>
              </Stack>
              <Typography variant="body2" fontWeight={600}>
                {currencyEGP(fundingSplit.provider)}{' '}
                <Typography component="span" variant="body2" >
                  ({fundingSplit.providerPercent}%)
                </Typography>
              </Typography>
            </Stack>
            <Divider sx={{ my: 0.5 }} />
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ py: 1.25 }}>
              <Typography variant="body2" fontWeight={600}>
                Total
              </Typography>
              <Typography variant="body2" fontWeight={700}>
                {currencyEGP(fundingSplit.total)}{' '}
                <Typography component="span" variant="body2" color="#535353">
                  (100%)
                </Typography>
              </Typography>
            </Stack>
          </Stack>
        </Card>

        {/* Budget Rules */}
        <Card variant="outlined" sx={{ borderRadius: 2, borderColor: '#E8E8E8', p: 1.5, my: 2 }}>
          <Box sx={{ p: 1, my: 0.5, borderBottom: '1px solid #E8E8E8' }}>
            <Typography fontWeight={600}>Budget Rules</Typography>
          </Box>
          <Stack divider={<Divider />}>
            {budgetRules.map((rule, i) => (
              <Stack key={rule.label} direction="row" justifyContent="space-between" alignItems="center" sx={{ py: 1.25, gap: 1, flexWrap: 'wrap' }}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Box
                    sx={{
                      width: 20,
                      height: 20,
                      borderRadius: '50%',
                      bgcolor:"#E8E8E8",
                      color: '#535353',
                      fontSize: 11,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    {i + 1}
                  </Box>
                  <Typography variant="body2">{rule.label}</Typography>
                </Stack>
                {rule.isChip ? (
                  <Chip size="small" label={rule.value} sx={{ bgcolor: '#ECF9F7', fontWeight: 600, px:1}} />
                ) : (
                  <Typography variant="body2" fontWeight={600}>
                    {rule.value}
                  </Typography>
                )}
              </Stack>
            ))}
          </Stack>
        </Card>

        {/* Alerts & Notifications */}
        <Card variant="outlined" sx={{ borderRadius: 2, borderColor: '#E8E8E8', p: 1.5, mt: 2, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ p: 1, my: 0.5, borderBottom: '1px solid #E8E8E8' }}>
            <Typography fontWeight={600}>Alerts & Notifications</Typography>
          </Box>
          <Stack spacing={1.5} sx={{ p: 1, flexGrow: 1 }}>
            {!budgetAlerts.hasActiveAlerts && (
              <Stack
                direction="row"
                spacing={1.5}
                alignItems="center"
                sx={{ bgcolor: '#ECF9F7', borderRadius: 2, p: 1.5 }}
              >
                <FaCheckCircle size={25} color="#3FBEAC" />
                <Box>
                  <Typography variant="body2" fontWeight={700} color="#159D89">
                    No active alerts
                  </Typography>
                  <Typography variant="caption" color="#535353">
                    Everything looks good!
                  </Typography>
                </Box>
              </Stack>
            )}
            {budgetAlerts.notifications.map((note, i) => (
              <Stack key={i} direction="row" spacing={1.5} alignItems="flex-start" sx={{ bgcolor: '#F8F8F8', borderRadius: 2, p: 1.5 }}>
                <IoMdInformationCircleOutline size={25} color="#535353" style={{ marginTop: 2, flexShrink: 0 }} />
                <Typography variant="body2" color="#535353">
                  {note}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Card>
        </Box>
      </Grid>
    </Grid>
  );
};

export default BudgetUsageTab;